import { useEffect } from "react";
import { Route, Router, Switch, useLocation } from "wouter";
import Console from "./components/Console";
import Login from "./components/Login";
import { useAuth } from "./authentication/AuthProvider";
import LoggedOff from "./components/LoggedOff";
import Register from "./components/Register";
import EmailLoginCompletion from "./components/EmailLoginCompletion";
import WaitForLoginEmail from "./components/WaitForLoginEmail";
import LoginWithPassword from "./components/LoginWithPassword";
import Unsubscribe from "./components/Unsubscribe";
import ErrorPage from "./components/ErrorPage";
import PersonalizedLogin from "./components/PersonalizedLogin";

let blockNav = false;
let suspendBlock = false;
export const SetBlockNav = (isBlocked: boolean) => {
  if (isBlocked === blockNav) return;
  if (isBlocked) {
    // protective buffer to recover from back button press
    window.history.pushState(null, "", window.location.href);
  } else {
    window.history.back();
  }
  blockNav = isBlocked;
};

export const IsBlockNav = () => blockNav;

function App() {
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (blockNav) {
        event.preventDefault();
      }
    };

    const handlePopstate = (event: PopStateEvent) => {
      event.preventDefault();
      if (blockNav) {
        if (suspendBlock) {
          // Suspend happens when in the middle of prompting for unsaved changes
          suspendBlock = false;
          return;
        }
        if (
          window.confirm("You have unsaved changed. Do you want to leave???")
        ) {
          SetBlockNav(false);
          // pop the top 2 history URLs
          // They should both point to the current page.
          // window.history.go(-2);
          // Behavior is erratic -- to be safe do a full reload.
          // window.location.reload();
          return;
        }
        // PROBLEM: history.forward() has a delayed action, so we try to block it!!
        // We got here because of the back button.
        // By the time we got here, the protective history record was popped off the stack.
        // We need to put it back.
        SetBlockNav(true);
        suspendBlock = true;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopstate);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopstate);
    };
  }, []);
  const { user } = useAuth();
  const [location, setLocation] = useLocation();
  const useLocationWithConfirmation = (): [string, (loc: string) => void] => {
    return [
      location,
      (newLocation: string) => {
        if (newLocation === location) return;
        if (blockNav) {
          if (
            window.confirm("You have unsaved changed. Do you want to leave??")
          ) {
            SetBlockNav(false);
            // We have two instances of the current page in history.
            // SetBlockNav(false) gets rid of one.
            // We need to get rid of the other.
            setLocation(newLocation);
            return;
          }
          return;
        }
        setLocation(newLocation);
      },
    ];
  };

  return (
    <Router hook={useLocationWithConfirmation}>
      <Switch>
        <Route path="/error">
          {() => {
            return <ErrorPage />;
          }}
        </Route>
        <Route path="/register/:email">
          {(params) => {
            return <Register email={params.email} />;
          }}
        </Route>
        <Route path="/register">
          {() => {
            return <Register />;
          }}
        </Route>
        <Route path="/login">
          {() => {
            return <Login />;
          }}
        </Route>
        <Route path="/loginWithPassword/:email">
          {(params) => {
            return <LoginWithPassword email={params.email} />;
          }}
        </Route>
        <Route path="/u/:guestGuid">
          {(params) => {
            return <Unsubscribe guestGuid={params.guestGuid} />;
          }}
        </Route>
        <Route path="/logoff">
          {() => {
            return <LoggedOff />;
          }}
        </Route>
        <Route path="/emailLoginCompletion">
          {() => {
            return <EmailLoginCompletion />;
          }}
        </Route>
        <Route path="/waitForLoginEmail">
          {() => {
            return <WaitForLoginEmail />;
          }}
        </Route>
        <Route path="/pel/:guestGuid">
          {(params) => {
            return <PersonalizedLogin guestGuid={params.guestGuid} />;
          }}
        </Route>
        <Route path="*">
          {() => {
            if (user == null) {
              window.localStorage.setItem("intendedUrl", location);
              return <Login />;
            }
            return <Console />;
          }}
        </Route>
      </Switch>
    </Router>
  );
}

export default App;
